import React from "react";
import { Guid } from "guid-typescript";

import PageSpecification from "../../types/system/PageSpecification";

import "./GridPager.scss";
import DropDown from "../inputform/DropDown";

interface GridPagerProps {
    mask?: boolean;
    specification: PageSpecification;
    onChange?: (specification: PageSpecification) => void;
}

class GridPager extends React.Component<GridPagerProps> {
    sizes = [10, 20, 40, 50, 100, 200];

    iteratePages(render: (index: number) => JSX.Element): JSX.Element[] {
        const elements = [] as JSX.Element[];
        const totalPages = Math.ceil(
            this.props.specification.total / this.props.specification.size,
        );
        const position = this.props.specification.page;

        for (let index = 0; index < totalPages; index++) {
            if (index < 1) {
                elements.push(render(index + 1));
                continue;
            } else if (
                index >= position - 1 &&
                (index < position + 2 || index < 5)
            ) {
                if (index === position - 1 && index > 1)
                    elements.push(render(-1));
                elements.push(render(index + 1));
                if (
                    (index === position + 1 &&
                        index < totalPages - 2 &&
                        index > 3) ||
                    (position < index && index === 4)
                )
                    elements.push(render(-1));
                continue;
            } else if (index > totalPages - 2) {
                elements.push(render(index + 1));
                continue;
            }
        }

        return elements;
    }

    changePage(increment: number, absolute: boolean) {
        const totalPages =
            Math.ceil(
                this.props.specification.total / this.props.specification.size,
            ) - 1;

        let newPage = absolute
            ? increment
            : this.props.specification.page + increment;
        newPage = Math.min(Math.max(newPage, 0), totalPages);

        if (newPage >= 0) {
            this.props.onChange?.({
                ...this.props.specification,
                page: newPage,
            });
        }
    }

    render(): React.ReactNode {
        const totalPages =
            Math.ceil(
                this.props.specification.total / this.props.specification.size,
            ) - 1;

        return (
            <div
                className={`grid-pager-container${
                    this.props.mask ? " masked" : ""
                }`}>
                <div className="grid-pager">
                    <div className="pages">
                        <button
                            tabIndex={
                                this.props.specification.page > 0 ? 0 : -1
                            }
                            className={
                                this.props.specification.page > 0
                                    ? "selectable"
                                    : ""
                            }
                            onClick={this.changePage.bind(this, -1, false)}>
                            <i>arrow_left</i>
                        </button>
                        <button
                            tabIndex={
                                this.props.specification.page < totalPages
                                    ? 0
                                    : -1
                            }
                            className={
                                this.props.specification.page < totalPages
                                    ? "selectable"
                                    : ""
                            }
                            onClick={this.changePage.bind(this, 1, false)}>
                            <i>arrow_right</i>
                        </button>
                        {this.iteratePages(index => {
                            return (
                                <button
                                    key={Guid.create().toString()}
                                    className={`${
                                        index ===
                                        this.props.specification.page + 1
                                            ? "selected"
                                            : ""
                                    }${index >= 0 ? " selectable" : ""}`}
                                    tabIndex={index === -1 ? null : 0}
                                    onClick={this.changePage.bind(
                                        this,
                                        index - 1,
                                        true,
                                    )}>
                                    {index === -1 ? "..." : index}
                                </button>
                            );
                        })}
                    </div>
                    <div className="page-info">
                        {this.props.specification.loading && (
                            <i className="fa-solid fa-arrows-rotate"></i>
                        )}
                        <span>Page Size&nbsp;</span>
                        <span>({this.props.specification.total})</span>
                        <DropDown
                            noType={true}
                            value={this.props.specification.size}
                            options={this.sizes.map((size, index) => ({
                                value: size,
                                text: String(size),
                                order: index + 1,
                            }))}
                            onChange={value =>
                                this.props.onChange({
                                    ...this.props.specification,
                                    size: Number(value),
                                })
                            }
                        />
                    </div>
                </div>
            </div>
        );
    }
}

export default GridPager;
